<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceSequenceMenu.vue: header (Sequences, entry count,
  hover readout, collapse), one SequenceRow per entry on a single shared scroll surface, footer with the active
  component, Clear and Create / Update (Create asks for a name first).
-->
<script setup lang="ts">
import { provide, ref } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Popover from 'primevue/popover';
import { sequenceEntryKey } from '@nanome-lite/state';
import { useSequenceStore } from '@/sequence/store';
import SequenceRow from '@/components/SequenceRow.vue';

const seq = useSequenceStore();

const namePrompt = ref<InstanceType<typeof Popover>>();
const nameValue = ref('');

// Single shared scroll surface for all rows; rows inject it to drive drag auto-scroll.
const scrollEl = ref<HTMLElement | null>(null);
provide('sequenceScroll', scrollEl);

function onValidate(event: Event) {
  if (!seq.hasSelection) return;
  if (seq.component.mode === 'update') {
    seq.updateComponent();
    return;
  }
  nameValue.value = '';
  namePrompt.value?.show(event);
}

// `autofocus` only fires on the first insert; focus the field every time the prompt opens.
function focusName() {
  document.querySelector<HTMLInputElement>('[data-testid=sequence-name]')?.focus();
}

function submitName() {
  const name = nameValue.value.trim();
  if (!name) return;
  seq.createComponent(name);
  namePrompt.value?.hide();
}
</script>

<template>
  <div class="h-full flex flex-col bg-ground of-hidden" data-testid="sequence-menu">
    <!-- Header -->
    <div class="shrink-0 flex items-center gap-2 px-3 py-2 b-b b-section">
      <span class="i-tabler-mist" />
      <span class="text-sm font-medium">Sequences</span>
      <span v-if="seq.entries.length" class="text-xs op-60">
        {{ seq.entries.length }} {{ seq.entries.length === 1 ? 'entry' : 'entries' }}
      </span>

      <span class="flex-1 truncate text-right text-xs op-70" data-testid="sequence-readout">{{ seq.readout }}</span>

      <Button v-tooltip.top="'Collapse'" icon="i-tabler-chevron-down" size="small" severity="secondary" text rounded aria-label="Collapse" @click="seq.isCollapsed = true" />
    </div>

    <!-- Rows (single shared scroll surface: global horizontal + vertical) -->
    <div ref="scrollEl" class="min-h-0 flex-1 of-auto" @pointerleave="seq.clearHover()">
      <div v-if="!seq.entries.length" class="h-full grid place-items-center text-sm op-60">
        No sequence data
      </div>
      <div v-else class="w-max min-w-full">
        <SequenceRow v-for="entry in seq.entries" :key="sequenceEntryKey(entry)" :entry="entry" />
      </div>
    </div>

    <!-- Footer -->
    <div class="h-12 shrink-0 flex items-center justify-end gap-2 px-3 b-0 b-t b-solid b-content">
      <Button
        v-if="seq.component.serial >= 0"
        v-tooltip.top="'Clear component to update'"
        :label="seq.component.name"
        size="small"
        severity="secondary"
        @click="seq.clearActiveComponent()"
      />
      <Button label="Clear" size="small" severity="secondary" :disabled="!seq.hasSelection" rounded @click="seq.clearSelection()" />
      <Button
        :label="seq.component.mode === 'update' ? 'Update' : 'Create'"
        size="small"
        :disabled="!seq.hasSelection"
        rounded
        data-testid="sequence-validate"
        @click="onValidate"
      />
    </div>

    <Popover ref="namePrompt" pt:content="flex flex-col gap-2" @show="focusName">
      <div class="text-sm">
        Name component
      </div>
      <form class="flex gap-2" @submit.prevent="submitName">
        <InputText v-model="nameValue" class="rd-5" size="small" autofocus data-testid="sequence-name" />
        <Button icon="i-tabler-check" type="submit" rounded aria-label="Create component" />
      </form>
    </Popover>
  </div>
</template>
